import { Link } from "react-router-dom";
import { ArrowRight, Check, ShieldCheck, Clock, MapPin, Video, HeartPulse, BadgeCheck } from "lucide-react";
import SEO from "../components/SEO";
import Reveal, { Stagger, StaggerItem } from "../components/Reveal";
import ServiceCard from "../components/ServiceCard";
import FaqList from "../components/FaqList";
import CTABand from "../components/CTABand";
import SectionHead from "../components/SectionHead";
import AnimatedTitle from "../components/AnimatedTitle";
import CountUp from "../components/CountUp";
import Marquee from "../components/Marquee";
import { BRAND, SERVICES, CONDITIONS, INSURANCES, HOW_IT_WORKS, BENEFITS, FAQS, THERAPISTS, SESSION_LENGTH } from "../data/site";
import { COUNTIES, REGIONS } from "../data/counties";
import { organizationSchema, faqSchema } from "../lib/schema";
import { unsplash, unsplashSrcSet } from "../lib/img";

const STATS = [
  { to: 21, suffix: "", label: "NJ counties served" },
  { to: 10, suffix: "+", label: "Years of clinical experience" },
  { to: 8, suffix: "", label: "Specialized services" },
  { to: 7, suffix: "", label: "Days a week availability" },
];

const BENEFIT_ICONS = [MapPin, HeartPulse, Clock, ShieldCheck];

export default function Home() {
  const homeFaqs = FAQS.slice(0, 5);
  const reema = THERAPISTS[0];
  return (
    <>
      <SEO path="/" schema={[organizationSchema(), faqSchema(homeFaqs)]} />

      {/* HERO */}
      <section className="relative overflow-hidden pt-10 pb-20 md:pt-16 md:pb-28" data-testid="home-hero">
        <div aria-hidden="true" className="blob blob-a -top-32 -left-24 h-[420px] w-[420px] bg-[#dfe6d8]" />
        <div aria-hidden="true" className="blob blob-b top-40 right-[-140px] h-[460px] w-[460px] bg-[#e9eee3]" />
        <div className="container-site relative grid lg:grid-cols-12 gap-14 items-center">
          <div className="lg:col-span-6">
            <span
              className="fade-in chip !bg-white/80 backdrop-blur !border-[var(--line)] !text-[var(--sage-dark)] font-semibold"
              data-testid="hero-badge"
            >
              <span className="pulse-dot h-2 w-2 rounded-full bg-[var(--sage)]" /> Virtual therapy in all 21 NJ counties
            </span>
            <AnimatedTitle
              text="Online therapy for all of"
              accent="New Jersey."
              testid="hero-title"
              baseDelay={80}
              className="mt-6 text-[2.75rem] leading-[1.02] sm:text-6xl md:text-[4.4rem] md:leading-[0.98] tracking-[-0.03em]"
            />
            <p className="slide-in mt-7 text-lg md:text-xl leading-relaxed text-body max-w-xl" style={{ animationDelay: "250ms" }}>
              Licensed, in-network therapists for anxiety, depression, teens, and families — wherever you feel most comfortable.
            </p>
            <div className="fade-in mt-9 flex flex-col sm:flex-row gap-3" style={{ animationDelay: "580ms" }}>
              <Link to="/contact" className="btn btn-primary !px-7 !py-4" data-testid="hero-cta-primary">
                Book a Session <ArrowRight className="h-4 w-4" />
              </Link>
              <Link to="/services" className="btn btn-secondary !px-7 !py-4" data-testid="hero-cta-secondary">
                Explore services
              </Link>
            </div>
            <ul className="fade-in mt-9 grid sm:grid-cols-2 gap-x-6 gap-y-3 text-[15px] text-[var(--ink)]" style={{ animationDelay: "700ms" }}>
              {["In-network with major insurance", "Evenings & weekends", "Secure, HIPAA-compliant video", "Kids, teens & adults"].map((t) => (
                <li key={t} className="flex items-center gap-2.5">
                  <span className="grid h-5 w-5 place-items-center rounded-full bg-[var(--sage-tint)]">
                    <Check className="h-3 w-3 text-[var(--sage)]" strokeWidth={3} />
                  </span>
                  {t}
                </li>
              ))}
            </ul>
          </div>

          <div className="lg:col-span-6 relative">
            <div className="relative overflow-hidden rounded-[2rem] aspect-[4/3.4] bg-[var(--sage-tint)] shadow-[0_40px_80px_-40px_rgba(29,33,28,.45)]">
              <img
                src={unsplash(BRAND.heroImage, 1080)}
                srcSet={unsplashSrcSet(BRAND.heroImage)}
                sizes="(min-width: 1024px) 560px, 92vw"
                width="1080"
                height="918"
                alt="Woman at home on her laptop during an online therapy session"
                className="kenburns h-full w-full object-cover object-[50%_80%]"
                fetchPriority="high"
                decoding="async"
              />
            </div>
            <div
              className="bob absolute -bottom-7 left-3 sm:-left-8 card shadow-[0_24px_50px_-24px_rgba(29,33,28,.4)] px-5 py-4 flex items-center gap-3"
              data-testid="hero-floating-card"
            >
              <span className="grid h-11 w-11 place-items-center rounded-full bg-[var(--sage)] text-white">
                <Video className="h-5 w-5" />
              </span>
              <div>
                <div className="text-sm font-semibold text-[var(--ink)]">Sessions from home</div>
                <div className="text-xs text-body">Sussex to Cape May</div>
              </div>
            </div>
            <div className="bob bob-delay absolute -top-5 right-3 sm:-right-5 card shadow-[0_24px_50px_-24px_rgba(29,33,28,.4)] px-4 py-3 hidden sm:flex items-center gap-2.5">
              <BadgeCheck className="h-5 w-5 text-[var(--sage)]" />
              <span className="text-xs font-semibold text-[var(--ink)]">Verified on Psychology Today</span>
            </div>
          </div>
        </div>
      </section>

      {/* INSURANCE MARQUEE */}
      <section className="border-y border-[var(--line)] bg-white" data-testid="insurance-strip">
        <div className="container-site py-7 flex flex-col md:flex-row md:items-center gap-4 md:gap-10">
          <span className="text-xs font-semibold uppercase tracking-[0.16em] text-body shrink-0">In-network with</span>
          <Marquee items={INSURANCES} className="flex-1" />
        </div>
      </section>

      {/* STATS */}
      <section className="section !pb-6" data-testid="home-stats">
        <div className="container-site">
          <Stagger className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {STATS.map((s) => (
              <StaggerItem key={s.label} className="card p-6 md:p-7">
                <CountUp to={s.to} suffix={s.suffix} className="font-display text-5xl md:text-6xl text-[var(--sage)]" />
                <p className="mt-2 text-sm text-body">{s.label}</p>
              </StaggerItem>
            ))}
          </Stagger>
        </div>
      </section>

      {/* SERVICES */}
      <section className="section" data-testid="home-services">
        <div className="container-site">
          <Reveal>
            <SectionHead
              eyebrow="Services"
              title={<>Virtual care for <em>every</em> stage of life</>}
              body="Evidence-based therapy online from licensed NJ clinicians."
              link={{ to: "/services", label: "All services" }}
            />
          </Reveal>
          <Stagger className="mt-10 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {SERVICES.map((s) => (
              <StaggerItem key={s.slug} className="h-full">
                <ServiceCard service={s} />
              </StaggerItem>
            ))}
          </Stagger>
          <Reveal className="mt-8 flex flex-wrap items-center gap-2.5" data-testid="home-conditions">
            <span className="text-sm font-medium text-body mr-1">Common concerns:</span>
            {CONDITIONS.map((c) => (
              <Link key={c.slug} to={`/conditions/${c.slug}`} className="chip" data-testid={`condition-chip-${c.slug}`}>
                {c.title} therapy
              </Link>
            ))}
          </Reveal>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="section bg-white border-y border-[var(--line)]" data-testid="home-how">
        <div className="container-site grid lg:grid-cols-12 gap-12">
          <Reveal className="lg:col-span-4">
            <span className="eyebrow">How it works</span>
            <h2 className="mt-3 text-4xl md:text-5xl text-balance">
              Getting started takes <em>minutes.</em>
            </h2>
            <p className="mt-4 text-body leading-relaxed">No waiting rooms, no commute — just a simple path to the right therapist.</p>
          </Reveal>
          <Stagger as="ol" stagger={0.12} className="lg:col-span-8 grid md:grid-cols-3 gap-4">
            {HOW_IT_WORKS.map((s, i) => (
              <StaggerItem as="li" key={s.title} className="card card-hover h-full p-7 relative overflow-hidden">
                <span aria-hidden="true" className="absolute -right-2 -top-6 font-display text-[7rem] leading-none text-[var(--sage-tint)] select-none">
                  {i + 1}
                </span>
                <span className="relative grid h-10 w-10 place-items-center rounded-full bg-[var(--sage)] text-white text-sm font-semibold">
                  0{i + 1}
                </span>
                <h3 className="relative mt-5 text-xl">{s.title}</h3>
                <p className="relative mt-2 text-sm leading-relaxed text-body">{s.body}</p>
              </StaggerItem>
            ))}
          </Stagger>
        </div>
      </section>

      {/* WHY ONLINE */}
      <section className="section" data-testid="home-benefits">
        <div className="container-site grid lg:grid-cols-2 gap-14 items-center">
          <Reveal className="relative order-last lg:order-first">
            <div className="overflow-hidden rounded-[2rem] aspect-[5/4] bg-[var(--sage-tint)]">
              <img
                src={unsplash(BRAND.secondaryImage, 900)}
                srcSet={unsplashSrcSet(BRAND.secondaryImage, [480, 768, 1080])}
                sizes="(min-width: 1024px) 540px, 100vw"
                width="900"
                height="720"
                alt="Smiling client waving during a virtual counseling session"
                className="h-full w-full object-cover transition-transform [transition-duration:1200ms] hover:scale-105"
                loading="lazy"
                decoding="async"
              />
            </div>
            <div className="bob absolute -bottom-6 right-4 card px-5 py-4 shadow-[0_24px_50px_-24px_rgba(29,33,28,.4)]">
              <div className="font-display text-3xl text-[var(--sage)]" data-testid="session-length">{SESSION_LENGTH}</div>
              <div className="text-xs text-body">Typical session length</div>
            </div>
          </Reveal>
          <div>
            <Reveal>
              <span className="eyebrow">Why online therapy</span>
              <h2 className="mt-3 text-4xl md:text-5xl text-balance">
                Real support that fits into <em>real life.</em>
              </h2>
            </Reveal>
            <Stagger className="mt-9 grid sm:grid-cols-2 gap-7">
              {BENEFITS.map((b, i) => {
                const Icon = BENEFIT_ICONS[i];
                return (
                  <StaggerItem key={b.title}>
                    <span className="grid h-11 w-11 place-items-center rounded-xl bg-[var(--sage-tint)] text-[var(--sage)]">
                      <Icon className="h-5 w-5" />
                    </span>
                    <h3 className="mt-4 text-lg">{b.title}</h3>
                    <p className="mt-1.5 text-sm leading-relaxed text-body">{b.body}</p>
                  </StaggerItem>
                );
              })}
            </Stagger>
          </div>
        </div>
      </section>

      {/* AREAS */}
      <section className="section bg-white border-y border-[var(--line)]" data-testid="home-areas">
        <div className="container-site">
          <Reveal>
            <SectionHead
              eyebrow="Areas we serve"
              title={<>Wherever you are in <em>New Jersey</em></>}
              body="Virtual sessions for anyone located in New Jersey, across all 21 counties."
              link={{ to: "/areas-we-serve", label: "View all areas" }}
            />
          </Reveal>
          <div className="mt-10 grid md:grid-cols-3 gap-8">
            {REGIONS.map((r) => (
              <div key={r}>
                <h3 className="font-ui text-sm font-semibold uppercase tracking-[0.14em] text-body">
                  {r}
                </h3>
                <Stagger stagger={0.04} className="mt-4 flex flex-wrap gap-2">
                  {COUNTIES.filter((c) => c.region === r).map((c) => (
                    <StaggerItem key={c.slug}>
                      <Link to={`/areas-we-serve/${c.slug}`} className="chip" data-testid={`home-county-${c.slug}`}>
                        <MapPin className="h-3.5 w-3.5 text-[var(--sage)]" /> {c.name}
                      </Link>
                    </StaggerItem>
                  ))}
                </Stagger>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CLINICIAN */}
      <section className="section" data-testid="home-clinician">
        <div className="container-site">
          <Reveal className="card p-6 md:p-10">
            <div>
              <span className="eyebrow">Meet our founder</span>
              <h2 className="mt-3 text-3xl md:text-4xl">
                {reema.name}, <span className="text-body text-2xl md:text-3xl">{reema.credentials}</span>
              </h2>
              <p className="mt-4 text-body leading-relaxed max-w-2xl">{reema.intro}</p>
              <Link to="/team" className="mt-5 inline-flex items-center gap-2 text-sm font-semibold text-[var(--sage)] hover:gap-3 transition-all" data-testid="home-about-link">
                Meet the team <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      {/* FAQ */}
      <section className="section !pt-4" data-testid="home-faq">
        <div className="container-site grid lg:grid-cols-12 gap-10">
          <Reveal className="lg:col-span-4">
            <span className="eyebrow">FAQ</span>
            <h2 className="mt-3 text-4xl md:text-5xl">
              Questions, <em>answered.</em>
            </h2>
            <Link to="/faq" className="mt-5 inline-flex items-center gap-2 text-sm font-semibold text-[var(--sage)]">
              See all FAQs <ArrowRight className="h-4 w-4" />
            </Link>
          </Reveal>
          <Reveal delay={0.1} className="lg:col-span-8">
            <FaqList faqs={homeFaqs} testid="home-faq-list" />
          </Reveal>
        </div>
      </section>

      <CTABand />
    </>
  );
}
